import React, { useEffect, useMemo, useState } from 'react';
import { useIeKitLabels } from '@/hooks/useIeKitLabels';
import { ViewerGuardedButton } from '@/components/shared/ViewerGuardedButton';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import type { IEItem, CatalogSizingOption } from '@/services/iem';
import { logger } from '@equip/utils/logger';

const log = logger.child({ component: 'UpdateThrItemDialog' });

type UpdateAction = 'extend' | 'convert' | 'convert_to_sized_asset';

export interface AllocationUpdateItem {
  id: string;
  handReceipt: 'PHR' | 'THR' | 'PnP';
  item: string;
  nsn: string;
  lin?: string | null;
  dueDate?: string;
  catalogId?: string | null;
  parentCatalogId?: string | null;
}

export interface UpdateThrItemDialogProps {
  open: boolean;
  item: AllocationUpdateItem | null;
  onClose: () => void;
  onExtend: (input: { handReceiptId: string; newDueDate: string; note: string }) => Promise<void>;
  onConvert: (input: {
    handReceiptId: string;
    approvalReference?: string;
    approvalConfirmed: boolean;
    note?: string;
  }) => Promise<void>;
  onConvertToSizedAsset: (input: {
    handReceiptId: string;
    targetCatalogId: string;
    justification?: string;
  }) => Promise<void>;
  ieItems: IEItem[];
  sizeVariants: CatalogSizingOption[];
  isExtendPending?: boolean;
  isConvertPending?: boolean;
  isConvertToSizedPending?: boolean;
  isIePending?: boolean;
  isSizeVariantsPending?: boolean;
}

const parseDate = (value: string | null | undefined): Dayjs | null => {
  if (!value || value === '--') {
    return null;
  }
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed : null;
};

const computeSuggestedDueDate = (item: AllocationUpdateItem): Dayjs => {
  const existing = parseDate(item.dueDate) ?? dayjs();
  const baseline = existing.isAfter(dayjs(), 'day') ? existing : dayjs();
  return baseline.add(90, 'day').startOf('day');
};

const formatDate = (value: string | null | undefined): string => {
  const parsed = parseDate(value ?? undefined);
  if (!parsed) {
    return '--';
  }
  return parsed.format('MMM D, YYYY');
};

const isNonIeItem = (item: AllocationUpdateItem | null, ieItems: IEItem[]): boolean => {
  if (!item) {
    return false;
  }

  const lin = item.lin?.trim().toLowerCase() ?? null;
  const nsn = item.nsn?.trim().toLowerCase() ?? null;

  if (!lin && !nsn) {
    return true;
  }

  return !ieItems.some((ieItem) => {
    const ieLin = ieItem.lin?.trim().toLowerCase() ?? null;
    const ieNsn = ieItem.nsn?.trim().toLowerCase() ?? null;

    if (lin && ieLin && lin === ieLin) {
      return true;
    }

    if (nsn && ieNsn && nsn === ieNsn) {
      return true;
    }

    return false;
  });
};

const formatVariantLabel = (variant: CatalogSizingOption): string => {
  const segments = [variant.displayName.trim()];

  if (variant.sizeLabel) {
    segments.push(`(${variant.sizeLabel})`);
  }

  if (variant.entityIdentifier) {
    segments.push(`• ${variant.entityIdentifier}`);
  }

  return segments.join(' ');
};

export const UpdateThrItemDialog: React.FC<UpdateThrItemDialogProps> = ({
  open,
  item,
  onClose,
  onExtend,
  onConvert,
  onConvertToSizedAsset,
  ieItems,
  sizeVariants,
  isExtendPending,
  isConvertPending,
  isConvertToSizedPending,
  isIePending,
  isSizeVariantsPending,
}) => {
  const [action, setAction] = useState<UpdateAction>('extend');
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [dueDateError, setDueDateError] = useState<string | null>(null);
  const [approvalConfirmed, setApprovalConfirmed] = useState(false);
  const [approvalTouched, setApprovalTouched] = useState(false);
  const [approvalReference, setApprovalReference] = useState('');
  const [extensionReason, setExtensionReason] = useState('');
  const [conversionReason, setConversionReason] = useState('');
  const [reasonError, setReasonError] = useState<string | null>(null);
  const [selectedVariantCatalogId, setSelectedVariantCatalogId] = useState('');
  const [variantError, setVariantError] = useState<string | null>(null);
  const [sizeConversionJustification, setSizeConversionJustification] = useState('');
  const MAX_REASON_LENGTH = 240;

  const isThrItem = item?.handReceipt === 'THR';
  const { label: ieKitLabel } = useIeKitLabels('front_window');
  const isProcessing = Boolean(isExtendPending || isConvertPending || isConvertToSizedPending);
  const ieItemsMemo = useMemo(() => ieItems, [ieItems]);
  const nonIe = isNonIeItem(item, ieItemsMemo);

  const availableActions = useMemo<UpdateAction[]>(() => {
    if (item?.handReceipt === 'PHR') {
      return ['convert_to_sized_asset'];
    }

    return ['extend', 'convert', 'convert_to_sized_asset'];
  }, [item?.handReceipt]);

  const sizeConversionState = useMemo(() => {
    if (!item) {
      return {
        disabled: true,
        message: 'Select an allocation to continue.',
      };
    }

    if (item.parentCatalogId) {
      return {
        disabled: true,
        message:
          'This allocation already uses a child size catalog. Convert to sized asset is unavailable.',
      };
    }

    if (!item.catalogId) {
      return {
        disabled: true,
        message: 'Catalog information is missing for this allocation.',
      };
    }

    if (isSizeVariantsPending) {
      return {
        disabled: true,
        message: 'Loading child size variants…',
      };
    }

    if (sizeVariants.length === 0) {
      return {
        disabled: true,
        message: 'No child size variants are configured for this catalog.',
      };
    }

    return {
      disabled: false,
      message: null,
    };
  }, [item, isSizeVariantsPending, sizeVariants.length]);

  useEffect(() => {
    if (open && item) {
      const defaultAction: UpdateAction =
        item.handReceipt === 'PHR' ? 'convert_to_sized_asset' : 'extend';
      setAction(defaultAction);
      setSelectedDate(item.handReceipt === 'THR' ? computeSuggestedDueDate(item) : null);
      setDueDateError(null);
      setApprovalConfirmed(false);
      setApprovalTouched(false);
      setApprovalReference('');
      setExtensionReason('');
      setConversionReason('');
      setReasonError(null);
      setSelectedVariantCatalogId('');
      setVariantError(null);
      setSizeConversionJustification('');
    } else if (!open) {
      setSelectedDate(null);
      setDueDateError(null);
      setApprovalConfirmed(false);
      setApprovalTouched(false);
      setApprovalReference('');
      setExtensionReason('');
      setConversionReason('');
      setReasonError(null);
      setSelectedVariantCatalogId('');
      setVariantError(null);
      setSizeConversionJustification('');
    }
  }, [open, item]);

  const handleClose = () => {
    if (!isProcessing) {
      onClose();
    }
  };

  const validateDueDate = (date: Dayjs | null): boolean => {
    if (!date) {
      setDueDateError('Select a future due date.');
      return false;
    }

    if (!date.isValid()) {
      setDueDateError('Invalid date value.');
      return false;
    }

    const today = dayjs().startOf('day');
    if (!date.endOf('day').isAfter(today)) {
      setDueDateError('Due date must be in the future.');
      return false;
    }

    setDueDateError(null);
    return true;
  };

  const validateReason = (value: string): boolean => {
    const trimmed = value.trim();
    if (!trimmed) {
      setReasonError(
        `Provide a brief reason for the ${action === 'extend' ? 'extension' : 'conversion'}.`,
      );
      return false;
    }
    if (trimmed.length > MAX_REASON_LENGTH) {
      setReasonError(
        `Keep the ${action === 'extend' ? 'extension' : 'conversion'} reason under ${MAX_REASON_LENGTH} characters.`,
      );
      return false;
    }
    setReasonError(null);
    return true;
  };

  const handleConfirm = async () => {
    if (!item || isProcessing) {
      return;
    }

    if (action === 'extend') {
      if (!isThrItem) {
        return;
      }

      const valid = validateDueDate(selectedDate);
      const reasonValid = validateReason(extensionReason);
      if (!valid || !reasonValid || !selectedDate) {
        return;
      }

      try {
        await onExtend({
          handReceiptId: item.id,
          newDueDate: selectedDate.endOf('day').toISOString(),
          note: extensionReason.trim(),
        });
        onClose();
      } catch (error) {
        log.error('Failed to extend THR due date', { error: String(error) });
      }
      return;
    }

    if (action === 'convert') {
      if (!isThrItem) {
        return;
      }

      if (!approvalConfirmed) {
        setApprovalTouched(true);
        return;
      }

      const convReasonValid = validateReason(conversionReason);
      if (!convReasonValid) {
        return;
      }

      try {
        await onConvert({
          handReceiptId: item.id,
          approvalReference: approvalReference.trim() || undefined,
          approvalConfirmed: true,
          note: conversionReason.trim() || undefined,
        });
        onClose();
      } catch (error) {
        log.error('Failed to convert THR to PHR', { error: String(error) });
      }
      return;
    }

    if (sizeConversionState.disabled) {
      return;
    }

    if (!selectedVariantCatalogId) {
      setVariantError('Select a child size variant.');
      return;
    }

    try {
      await onConvertToSizedAsset({
        handReceiptId: item.id,
        targetCatalogId: selectedVariantCatalogId,
        justification: sizeConversionJustification.trim() || undefined,
      });
      onClose();
    } catch (error) {
      log.error('Failed to convert allocation to sized asset', { error: String(error) });
    }
  };

  const confirmLabel =
    action === 'extend'
      ? 'Extend due date'
      : action === 'convert'
        ? 'Convert to permanent (PHR)'
        : 'Convert to sized asset';

  const disableConfirm =
    !item ||
    (action === 'extend' &&
      (!isThrItem ||
        Boolean(dueDateError) ||
        Boolean(reasonError) ||
        !selectedDate ||
        extensionReason.trim().length === 0)) ||
    (action === 'convert' &&
      (!isThrItem ||
        !approvalConfirmed ||
        Boolean(isIePending) ||
        conversionReason.trim().length === 0)) ||
    (action === 'convert_to_sized_asset' &&
      (sizeConversionState.disabled || !selectedVariantCatalogId || Boolean(variantError)));

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="thr-update-title"
    >
      <DialogTitle id="thr-update-title">Update Allocation</DialogTitle>
      <DialogContent dividers>
        {item ? (
          <Stack spacing={2}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Item
              </Typography>
              <Typography variant="body1">{item.item}</Typography>
            </Box>
            <Stack
              direction="row"
              spacing={2}
              divider={<Divider orientation="vertical" flexItem />}
            >
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Hand Receipt
                </Typography>
                <Typography variant="body1">{item.handReceipt}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  NSN
                </Typography>
                <Typography variant="body1">{item.nsn}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  LIN
                </Typography>
                <Typography variant="body1">{item.lin ?? '—'}</Typography>
              </Box>
              {isThrItem && (
                <Box>
                  <Typography variant="subtitle2" color="text.secondary">
                    Current Due Date
                  </Typography>
                  <Typography variant="body1">{formatDate(item.dueDate)}</Typography>
                </Box>
              )}
            </Stack>

            <FormControl fullWidth>
              <InputLabel id="thr-update-action-label">Update Action</InputLabel>
              <Select
                labelId="thr-update-action-label"
                label="Update Action"
                value={action}
                onChange={(event) => {
                  const value = event.target.value as UpdateAction;
                  setAction(value);
                  setDueDateError(null);
                  setReasonError(null);
                  setVariantError(null);
                  setSelectedVariantCatalogId('');

                  if (value === 'extend' && item) {
                    setSelectedDate(computeSuggestedDueDate(item));
                    setExtensionReason('');
                  }

                  if (value === 'convert') {
                    setApprovalTouched(false);
                  }
                }}
              >
                {availableActions.includes('extend') && (
                  <MenuItem value="extend">Extend due date</MenuItem>
                )}
                {availableActions.includes('convert') && (
                  <MenuItem value="convert">Convert to permanent (PHR)</MenuItem>
                )}
                {availableActions.includes('convert_to_sized_asset') && (
                  <MenuItem value="convert_to_sized_asset">Convert to sized asset</MenuItem>
                )}
              </Select>
            </FormControl>

            {action === 'extend' && isThrItem && (
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="New due date"
                  value={selectedDate}
                  onChange={(value) => {
                    setSelectedDate(value);
                    if (dueDateError) {
                      setDueDateError(null);
                    }
                  }}
                  disablePast
                  minDate={dayjs().add(1, 'day')}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      helperText: dueDateError ?? 'Choose any future date (default +90 days).',
                      error: Boolean(dueDateError),
                    },
                  }}
                />
                <TextField
                  label="Extension reason"
                  value={extensionReason}
                  onChange={(event) => {
                    const next = event.target.value;
                    setExtensionReason(next);
                    if (reasonError) {
                      validateReason(next);
                    }
                  }}
                  onBlur={() => validateReason(extensionReason)}
                  error={Boolean(reasonError)}
                  helperText={
                    reasonError ??
                    `Share the approval rationale (max ${MAX_REASON_LENGTH} characters).`
                  }
                  fullWidth
                  multiline
                  minRows={2}
                  inputProps={{ maxLength: MAX_REASON_LENGTH }}
                />
              </LocalizationProvider>
            )}

            {action === 'convert' && isThrItem && (
              <Stack spacing={1.5}>
                {Boolean(isIePending) && (
                  <Alert severity="info">Loading {ieKitLabel} data to verify eligibility…</Alert>
                )}
                {!isIePending && nonIe && (
                  <Alert severity="warning">
                    This item does not appear on the member&apos;s {ieKitLabel}. Confirm that you have the
                    required approval before converting it to a permanent hand receipt.
                  </Alert>
                )}
                <FormControl error={approvalTouched && !approvalConfirmed}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={approvalConfirmed}
                        onChange={(event) => {
                          setApprovalConfirmed(event.target.checked);
                          if (approvalTouched) {
                            setApprovalTouched(false);
                          }
                        }}
                      />
                    }
                    label={`I confirm non authorized ${ieKitLabel} approval has been received by OPS SGM/SQDN CSM or CDD`}
                  />
                  {approvalTouched && !approvalConfirmed && (
                    <FormHelperText>Approval acknowledgement is required.</FormHelperText>
                  )}
                </FormControl>
                <TextField
                  label="Approval reference (optional)"
                  value={approvalReference}
                  onChange={(event) => setApprovalReference(event.target.value)}
                  inputProps={{ maxLength: 120 }}
                  helperText="Record ticket, email, or memo reference supporting the approval."
                  fullWidth
                />
                <TextField
                  label="Conversion reason"
                  value={conversionReason}
                  onChange={(event) => {
                    const next = event.target.value;
                    setConversionReason(next);
                    if (reasonError) {
                      validateReason(next);
                    }
                  }}
                  onBlur={() => validateReason(conversionReason)}
                  error={Boolean(reasonError)}
                  helperText={
                    reasonError ??
                    `Share the approval rationale (max ${MAX_REASON_LENGTH} characters).`
                  }
                  fullWidth
                  multiline
                  minRows={2}
                  inputProps={{ maxLength: MAX_REASON_LENGTH }}
                />
              </Stack>
            )}

            {action === 'convert_to_sized_asset' && (
              <Stack spacing={1.5}>
                {sizeConversionState.message && (
                  <Alert severity={sizeConversionState.disabled ? 'warning' : 'info'}>
                    {sizeConversionState.message}
                  </Alert>
                )}

                <FormControl
                  fullWidth
                  error={Boolean(variantError)}
                  disabled={sizeConversionState.disabled}
                >
                  <InputLabel id="sized-asset-variant-label">Size variant</InputLabel>
                  <Select
                    labelId="sized-asset-variant-label"
                    label="Size variant"
                    value={selectedVariantCatalogId}
                    onChange={(event) => {
                      setSelectedVariantCatalogId(event.target.value);
                      if (variantError) {
                        setVariantError(null);
                      }
                    }}
                  >
                    {sizeVariants.map((variant) => (
                      <MenuItem key={variant.id} value={variant.id}>
                        {formatVariantLabel(variant)}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {variantError ??
                      'Choose the child size catalog to replace the currently assigned non-sized item.'}
                  </FormHelperText>
                </FormControl>

                <TextField
                  label="Justification"
                  placeholder="Enter justification for DA 4949"
                  value={sizeConversionJustification}
                  onChange={(event) => setSizeConversionJustification(event.target.value)}
                  multiline
                  minRows={2}
                  maxRows={4}
                  fullWidth
                  disabled={sizeConversionState.disabled || isProcessing}
                />
              </Stack>
            )}
          </Stack>
        ) : (
          <Typography color="text.secondary">Select an allocation item to update.</Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isProcessing}>
          Cancel
        </Button>
        <ViewerGuardedButton onClick={handleConfirm} variant="contained" disabled={disableConfirm}>
          {isProcessing ? 'Processing…' : confirmLabel}
        </ViewerGuardedButton>
      </DialogActions>
    </Dialog>
  );
};
